import { useCurrentProject } from 'src/providers/current-project.provider'
import { TFollowup, TPatientFollowup } from 'src/types/followup'
import { getFormattedDate } from 'src/utils/date-formatter.util'
import { twMerge } from 'src/utils/tw-merge.util'
import { useTranslation } from 'react-i18next'
import { useCurrentPatientFile } from './patient-file.provider'
import { useLocation, useParams } from 'react-router-dom'
import { LocalizedLink } from 'src/components/navigation/localized-link.component'
import { PlusRoundedIcon } from '@honestica/curecall-ui'

const getTranslatedLabel = (locale: string, label?: { fr?: string; en?: string }) => {
  if (!label) return null
  return label[locale as keyof typeof label] || label.fr || label.en || null
}

const getFollowupTabTitle = ({ followup, locale }: { followup: TFollowup; locale: string }) => {
  const tabTitleLabel = getTranslatedLabel(locale, followup?.configUI?.followupUI?.patientDetails?.tabTitle)

  return tabTitleLabel || followup.name
}

const FollowupsTabs = () => {
  const { project } = useCurrentProject()
  const { patient, patientFollowupsToShow, endedPatientFollowups } = useCurrentPatientFile()
  const { i18n } = useTranslation()
  const locale = i18n.language
  const { t } = useTranslation()

  const params = useParams()
  const currentPatientFollowupId = Number(params.patientFollowupId)
  const location = useLocation()
  const pathname = location.pathname

  const titleOccurrences = new Map<string, number>()
  for (const patientFollowup of [...(patientFollowupsToShow ?? []), ...(endedPatientFollowups ?? [])]) {
    const title = getFollowupTabTitle({ followup: patientFollowup.Followup, locale })
    titleOccurrences.set(title, (titleOccurrences.get(title) ?? 0) + 1)
  }

  const getFirstAppointmentDate = (patientFollowup: TPatientFollowup) => {
    const [firstPatientMedEvent] = (patient.PatientMedEvents ?? [])
      .filter((patientMedEvent) => patientMedEvent.PatientFollowupId === patientFollowup.id)
      .sort(
        (patientMedEvent1, patientMedEvent2) =>
          new Date(patientMedEvent1.date).getTime() - new Date(patientMedEvent2.date).getTime(),
      )
    return firstPatientMedEvent?.date ?? patientFollowup.startDate
  }

  const getTabTitle = (patientFollowup: TPatientFollowup) => {
    const title = getFollowupTabTitle({ followup: patientFollowup.Followup, locale })
    if (titleOccurrences.get(title) === 1) return title
    return t('pages.patient-page.followup-tab-title-with-date', {
      title,
      date: getFormattedDate(getFirstAppointmentDate(patientFollowup)),
    })
  }

  return (
    <ul className="c-flex c-mt-6 c-border-b-2 c-border-b-grey-300 c-flex-wrap c-gap-2">
      {patientFollowupsToShow?.map((patientFollowup) => {
        return (
          <li key={`followup-menu-item-${patientFollowup.id}`} className="c-flex c-mr-4">
            <LocalizedLink
              to={`/${project.id}/medical/patients/${patient.id}/${patientFollowup.id}`}
              className={twMerge(
                'c-py-1 c-text-grey-700 c-text-xs c-cursor-pointer c-min-w-fit c-w-max c-border-2 c-border-grey-300 c-border-dashed c-px-3 c-border-b-transparent c-rounded-t-lg',
                currentPatientFollowupId === patientFollowup.id &&
                  'c-border-b-4 c-border-solid c-border-b-primary c-text-primary',
              )}
            >
              {getTabTitle(patientFollowup)}
            </LocalizedLink>
          </li>
        )
      })}
      <li className="c-flex c-mr-4">
        <LocalizedLink
          to={`/${project.id}/medical/patients/${patient.id}/add-patient-followup`}
          className={twMerge(
            'c-py-1 c-text-grey-700 c-text-xs c-cursor-pointer c-min-w-fit c-w-max c-border-2 c-border-grey-300 c-border-dashed c-px-3 c-border-b-transparent c-rounded-t-lg',
            pathname.includes('add-patient-followup') &&
              'c-border-b-4 c-border-solid c-border-b-primary c-text-primary',
          )}
        >
          <PlusRoundedIcon className="tw-w-3 tw-inline-block tw-mr-1" />{' '}
          {t('pages.patient-page.add-a-followup')}
        </LocalizedLink>
      </li>
      {endedPatientFollowups?.map((patientFollowup) => {
        return (
          <li key={`followup-menu-item-${patientFollowup.id}`} className="c-flex c-mr-4">
            <LocalizedLink
              to={`/${project.id}/medical/patients/${patient.id}/${patientFollowup.id}`}
              className={twMerge(
                'c-py-1 c-text-grey-700 c-text-xs c-cursor-pointer c-min-w-fit c-w-max c-border-2 c-border-grey-300 c-border-dashed c-px-3 c-border-b-transparent c-rounded-t-lg',
                currentPatientFollowupId === patientFollowup.id &&
                  ' c-border-b-4 c-border-solid c-border-b-primary c-text-primary',
              )}
            >
              {getTabTitle(patientFollowup)} {t('pages.patient-page.ended')}
            </LocalizedLink>
          </li>
        )
      })}
    </ul>
  )
}

export default FollowupsTabs
